import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/motion";

const shelves = [
  {
    n: "01",
    title: "Letters from the Messy Middle",
    kind: "Articles & Reflections",
    copy: "Short, honest reflections on disruption, uncertainty, hope and becoming — written from the middle, not from the finish line.",
  },
  {
    n: "02",
    title: "The Toolkit, in Your Hands",
    kind: "Practical Tools",
    copy: "Printable, usable versions of the practical handles: grounding methods, journaling prompts, the reframe questions and more.",
  },
  {
    n: "03",
    title: "Stories That Walked",
    kind: "Real Stories",
    copy: "Accounts of people who reframed, repurposed, pivoted and grew — proof that the messy parts can become catalysts.",
    img: "/alex-panel.jpg",
  },
  {
    n: "04",
    title: "Leading Yourself First",
    kind: "Leadership Notes",
    copy: "Field notes on self-leadership, communication that connects, and growing the people who grow the work.",
  },
];

export default function Resources() {
  useEffect(() => {
    document.title = "Resources — People Growing People";
  }, []);

  return (
    <main data-testid="resources-page" className="pt-20">
      {/* HERO */}
      <section className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32">
          <MaskedLine delay={0.1}>
            <span className="text-xs uppercase tracking-[0.3em] text-stone">Resources</span>
          </MaskedLine>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-8xl tracking-tight leading-[0.95] mt-6 max-w-4xl">
            <MaskedLine delay={0.25}>Field notes</MaskedLine>
            <MaskedLine delay={0.37}>
              for the <span className="italic text-clay">middle.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/75 max-w-2xl">
              Articles, reflections and practical tools around growth, resilience,
              leadership and hope. They are being written slowly and honestly —
              the first shelves open soon.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMING SOON SHELVES */}
      <section data-testid="resources-shelves" className="border-b border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <div className="grid md:grid-cols-2 gap-8 md:gap-10">
            {shelves.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div
                  data-testid={`resource-card-${i + 1}`}
                  className="group border border-line bg-paper h-full flex flex-col hover:border-clay/50 transition-colors duration-300"
                >
                  {s.img && (
                    <div className="overflow-hidden border-b border-line">
                      <img
                        src={s.img}
                        alt="Alex in conversation on a Real Stories panel"
                        loading="lazy"
                        className="w-full aspect-[16/9] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                      />
                    </div>
                  )}
                  <div className="p-8 md:p-10 flex flex-col grow">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-serif text-3xl text-clay/60">{s.n}</span>
                      <span className="text-[10px] uppercase tracking-[0.25em] text-moss border border-moss/40 px-3 py-1.5">
                        Coming soon
                      </span>
                    </div>
                    <p className="text-xs uppercase tracking-[0.25em] text-stone mt-8">{s.kind}</p>
                    <h2 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight mt-3">
                      {s.title}
                    </h2>
                    <p className="text-sm md:text-base text-ink/65 mt-4 leading-relaxed">
                      {s.copy}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOTIFY / CTA */}
      <section className="bg-ink text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline className="text-paper/50">Meanwhile</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02] mt-6 max-w-4xl">
              The best resource is still{" "}
              <span className="italic text-clay">a conversation.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-paper/75 max-w-xl">
              Want to know when the first pieces land — or need something to hold
              onto today? Tell Alex where you are, and he'll point you to what
              helps.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/contact"
              data-testid="resources-cta-contact"
              className="group mt-12 inline-flex items-center gap-3 bg-clay text-paper text-xs uppercase tracking-[0.22em] px-10 py-5 hover:bg-paper hover:text-ink transition-colors duration-300"
            >
              Tell Me Where You Are
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
